<template>
  <Layout class="onboarding-upgrade16167">
    <Rive
      ref="riveRef"
      class="onboarding-upgrade16167__rive"
      :src="getImage(page.animation.src)"
      :width="page.animation.width"
      :height="page.animation.height"
      :animations="`${currentSection}`"
      @loop="nextSection"
      @stop="nextPage"
    />

    <Transition name="upgrade-title" mode="out-in">
      <div :key="currentSection" class="onboarding-upgrade16167__text">
        <h1 class="onboarding-upgrade16167__title">
          {{ page.title }}
          <span
            class="onboarding-upgrade16167__current-title"
            :style="{
              '--upgrade-color': colors[currentSection % colors.length],
            }"
            >{{ page.sectionTitles[currentSection] }}</span
          >
        </h1>

        <p class="onboarding-upgrade16167__subtitle">
          {{ page.sectionSubtitles[currentSection] }}
        </p>
      </div>
    </Transition>

    <p v-if="showFooter" class="onboarding-upgrade16167__footer">
      One app. Big results in every part of your life.
    </p>
  </Layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { getImage, QuestionnaireView } from '@/models/onboarding'
import { Upgrade16167Page } from '@/models/onboarding.types'
import { PublicInterface } from '@/models/interfaces'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const riveRef = ref<InstanceType<typeof Rive> | null>(null)

const currentSection = ref(0)
const showFooter = ref(true)

const page = computed(() => {
  return props.view.currentPage as Upgrade16167Page
})

const colors = ['#1F5BCF', '#5AA96B', '#FF7124']

function nextSection(): void {
  currentSection.value += 1
}

function nextPage(): void {
  const lastSection = page.value.sectionTitles.length - 1
  if (currentSection.value === lastSection) {
    showFooter.value = false
    props.view.showContinueButton()
  }
}
</script>

<style lang="scss" scoped>
.onboarding-upgrade16167 {
  // Extra space at the bottom to accomodate the footer
  // which is absolute positioned.
  padding-bottom: calc(var(--onboarding-padding) * 4);

  :deep(.onboarding-layout__default-slot) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__rive {
    max-width: 280px;
    height: auto;
    min-height: 150px;
    margin-bottom: 35px;
    @media (max-height: 650px) {
      margin-bottom: 16px;
    }
    @media (max-height: 580px) {
      max-width: 220px;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    text-align: center;
    @media (max-height: 580px) {
      gap: 12px;
    }
  }

  &__title {
    font-size: 32px;
    line-height: 36px;
  }

  &__current-title {
    display: block;
    text-decoration: underline dotted;
    text-decoration-thickness: 0.16em;
    text-underline-offset: 0.22em;
    color: var(--upgrade-color);
  }

  &__subtitle {
    font-size: 20px;
    line-height: 28px;
    // To prevent layout shifts when
    // the subtitle changes.
    min-height: 56px;
    @media (max-height: 580px) {
      font-size: 18px;
      line-height: 24px;
    }
  }

  &__footer {
    position: absolute;
    bottom: var(--onboarding-padding);
    left: 50%;
    transform: translateX(-50%);
    max-width: 247px;
    text-align: center;
  }

  ~ :deep(.onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
  }
}

// Cross fade the current title and subtitle.
.upgrade-title {
  &-enter-active,
  &-leave-active,
  &-enter-active *,
  &-leave-active * {
    transition: all 0.3s;
  }

  &-enter-from,
  &-leave-to {
    .onboarding-upgrade16167__current-title,
    .onboarding-upgrade16167__subtitle {
      opacity: 0;
    }
  }
}
</style>
